import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Chip,
  IconButton,
  Stack,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import PageHeader from '../../components/layout/PageHeader';
import UserFormModal from '../../components/user/UserFormModal';
import api from '../../services/api';

// User seed do sistema — não deve ser exibido nem excluído por UX.
const HIDDEN_USER_ID = 'be245531-f8bf-487d-b212-f7c56c44b51b';
// User "app default" que o app RN usa pra auto-login — pode editar mas não excluir.
const PROTECTED_USER_ID = '57306640-28bf-4c79-ad28-0bf95a14e90a';

const CONTAINER_SX = {
  maxWidth: 1600,
  margin: '0 auto',
  px: { xs: 2, md: 3 },
  py: 3,
};

const dataGridStyles = {
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #eee',
  },
  '& .MuiDataGrid-columnSeparator': { display: 'none' },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
};

export default function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);

  const openCreate = () => {
    setUserToEdit(null);
    setModalOpen(true);
  };

  const openEdit = (u) => {
    setUserToEdit(u);
    setModalOpen(true);
  };

  useEffect(() => {
    const isLogged = localStorage.getItem('token');
    if (!isLogged) navigate('/');
    else setIsAuthenticated(true);
  }, [navigate]);

  const loadUsers = async () => {
    if (!isAuthenticated) return;
    try {
      const response = await api.get('/users');
      setUsers(response.data.filter((u) => u.id !== HIDDEN_USER_ID));
    } catch (err) {
      console.error(err);
      alert('Falha ao carregar usuários, tente recarregar a página.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [isAuthenticated]);

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esse usuário?')) return;
    try {
      await api.delete(`/users/${id}`);
      alert('Usuário apagado com sucesso.');
      loadUsers();
    } catch (err) {
      console.error(err);
      alert('Falha ao apagar usuário, tente novamente.');
    }
  };

  const rows = users.map((u) => ({
    id: u.id,
    name: u.name,
    email: u.email,
    role: u.role,
    raw: u,
  }));

  const columns = [
    {
      field: 'name', headerName: 'Nome', flex: 1, minWidth: 160,
    },
    {
      field: 'email', headerName: 'E-mail', flex: 2, minWidth: 220,
    },
    {
      field: 'role',
      headerName: 'Permissão',
      width: 160,
      renderCell: (p) => (
        <Chip
          label={p.value}
          size="small"
          color={p.value === 'administrator' ? 'primary' : 'default'}
          variant="outlined"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'action',
      headerName: 'Ações',
      width: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            aria-label="Editar"
            onClick={() => openEdit(p.row.raw)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {p.row.id !== PROTECTED_USER_ID && (
            <IconButton
              size="small"
              aria-label="Excluir"
              onClick={() => handleDelete(p.row.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={CONTAINER_SX}>
      <PageHeader
        title="Usuários"
        description="Contas com acesso ao painel administrativo. Administradores gerenciam locais, beacons e outros usuários; operadores têm acesso limitado."
        action={(
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            Novo usuário
          </Button>
        )}
      />

      <Paper elevation={1} sx={{ borderRadius: 2, border: '1px solid #eee', overflow: 'hidden' }}>
        {isLoading ? (
          <Stack spacing={1} sx={{ p: 2 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rectangular" height={48} />
            ))}
          </Stack>
        ) : (
          <DataGrid
            sx={dataGridStyles}
            autoHeight
            rows={rows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
          />
        )}
      </Paper>

      <UserFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        userToEdit={userToEdit}
        onSaved={loadUsers}
      />
    </Box>
  );
}
